// tienda/HeroSection.jsx
// Focused ecommerce hero centered on Fast Max's logistics infrastructure.

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { brand, fadeUp } from "../shared/brand";

const DEFAULT_HERO_VIDEO_MP4 =
  "https://storage.googleapis.com/fastmax-images/lumaya/products/lumaya-hair-repair-mask/video/20260815232031-HeroVideReady.mp4";

// A WebM URL can be configured independently so supported browsers download the
// smaller format first. MP4 remains the compatibility fallback.
const HERO_VIDEO_WEBM = process.env.REACT_APP_HERO_VIDEO_WEBM_URL;
const HERO_VIDEO_MP4 = process.env.REACT_APP_HERO_VIDEO_MP4_URL || DEFAULT_HERO_VIDEO_MP4;

export function HeroSection() {
  return (
    <section
      className="relative isolate overflow-hidden border-b"
      style={{ background: brand.bg, borderColor: brand.line }}
    >
      <div
        className="absolute inset-0 -z-20"
        style={{
          background:
            `radial-gradient(circle at 12% 18%, ${brand.accentGold}2E, transparent 28%),` +
            `radial-gradient(circle at 88% 12%, ${brand.accentGreen}24, transparent 30%),` +
            `linear-gradient(135deg, ${brand.bg} 0%, #FFFFFF 52%, #F8EEF4 100%)`,
        }}
      />
      <div
        className="absolute inset-0 -z-10 opacity-40"
        style={{
          backgroundImage:
            `linear-gradient(${brand.line} 1px, transparent 1px), linear-gradient(90deg, ${brand.line} 1px, transparent 1px)`,
          backgroundSize: "64px 64px",
          maskImage: "linear-gradient(to bottom, black, transparent 88%)",
        }}
      />

      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-14 pt-12 md:pb-20 md:pt-16 lg:grid-cols-[0.88fr_1.12fr] lg:gap-16 lg:pb-24 lg:pt-20">
        <motion.div {...fadeUp} className="relative z-10 flex flex-col justify-center">
          <h1
            className="max-w-2xl text-4xl font-black leading-[1.02] tracking-[-0.04em] sm:text-5xl md:text-6xl"
            style={{ color: brand.ink }}
          >
            La infraestructura de delivery para{" "}
            <span style={{ color: brand.primary }}>hacer crecer tu ecommerce.</span>
          </h1>

          <p
            className="mt-6 max-w-xl text-base leading-relaxed md:text-lg"
            style={{ color: brand.muted }}
          >
            Gestiona entregas same-day, fulfillment, contraentrega, tracking y coordinación desde una
            plataforma creada para operar con más control y escalar sin fricción.
          </p>

          <div className="mt-8">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-pink-900/10 transition-transform hover:-translate-y-0.5"
              style={{ background: brand.primary }}
            >
              Crear cuenta gratis
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </motion.div>

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.08 }}
          className="relative lg:-mr-10"
        >
          <HeroVideo />
        </motion.div>
      </div>
    </section>
  );
}

function HeroVideo() {
  const videoRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    let isVisible = true;

    const syncPlayback = () => {
      if (isVisible && !document.hidden) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        syncPlayback();
      },
      { rootMargin: "160px" }
    );

    observer.observe(video);
    document.addEventListener("visibilitychange", syncPlayback);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", syncPlayback);
    };
  }, []);

  return (
    <div className="relative mx-auto max-w-3xl">
      <div
        className="absolute -inset-5 -z-10 rounded-[2.5rem] opacity-70 blur-3xl"
        style={{ background: `linear-gradient(135deg, ${brand.primary}30, ${brand.accentGold}24)` }}
      />

      <div
        className="relative overflow-hidden rounded-[1.75rem] border bg-white p-2 shadow-2xl shadow-pink-950/15 sm:p-3"
        style={{ borderColor: brand.line }}
      >
        <div
          className="relative aspect-video overflow-hidden rounded-[1.35rem]"
          style={{ background: `linear-gradient(135deg, ${brand.plum}, ${brand.primary})` }}
        >
          <video
            ref={videoRef}
            className={`absolute inset-0 size-full object-cover transition-opacity duration-500 ${
              isReady ? "opacity-100" : "opacity-0"
            }`}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            disablePictureInPicture
            controlsList="nodownload noremoteplayback"
            onCanPlay={() => setIsReady(true)}
            aria-label="Experiencia de entrega Fast Max"
          >
            {HERO_VIDEO_WEBM && <source src={HERO_VIDEO_WEBM} type="video/webm" />}
            <source src={HERO_VIDEO_MP4} type="video/mp4" />
            Tu navegador no puede reproducir este video.
          </video>
        </div>
      </div>
    </div>
  );
}
